import { TrendingUp, Package, DollarSign, AlertCircle, Warehouse } from "lucide-react";
import { LOW_STOCK_UI_THRESHOLD } from "./adminApi";

const ACCENTS = { gold: "var(--bl-gold)", green: "var(--bl-green)", blue: "var(--bl-blue)" };

const StatCard = ({ label, value, sub, icon: Icon, accent = "blue", testid }) => (
  <div data-testid={testid} className="border border-white/8 p-5 bg-white/[0.015]">
    <div className="flex items-start justify-between">
      <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">{label}</div>
      {Icon && <Icon size={16} style={{ color: ACCENTS[accent] || ACCENTS.blue }} />}
    </div>
    <div className="font-display text-white text-3xl mt-3">{value}</div>
    {sub && <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mt-2">{sub}</div>}
  </div>
);

const Sparkline = ({ series }) => {
  const max = Math.max(1, ...series.map(s => s.revenue));
  return (
    <div className="flex items-end gap-2 h-24">
      {series.map((s) => {
        const pct = (s.revenue / max) * 100;
        return (
          <div key={s.date} className="flex-1 flex flex-col items-center gap-2">
            <div className="w-full flex items-end h-20">
              <div
                className="w-full rounded-t-sm transition-all"
                style={{
                  height: `${Math.max(pct, s.revenue > 0 ? 6 : 2)}%`,
                  background: s.revenue > 0 ? "linear-gradient(180deg, var(--bl-gold), rgba(76,157,255,0.35))" : "rgba(255,255,255,0.06)",
                }}
                title={`A$${s.revenue.toFixed(2)} · ${s.orders} orders`}
              />
            </div>
            <div className="font-mono text-[9px] tracking-[0.15em] uppercase text-white/40">
              {new Date(s.date).toLocaleDateString(undefined, { weekday: "short" })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TopProducts = ({ products }) => {
  if (products.length === 0) return <div className="text-white/40 py-4 text-sm">No sales data yet.</div>;
  const max = products[0].revenue || 1;
  return (
    <div className="space-y-3">
      {products.map((p) => (
        <div key={p.name} data-testid={`top-product-${p.name}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="text-white text-sm">{p.name}</div>
            <div className="font-mono text-[11px] text-white/60">
              <span className="text-[color:var(--bl-gold)]">A${p.revenue.toFixed(2)}</span> · {p.units} units
            </div>
          </div>
          <div className="h-1.5 bg-white/5 overflow-hidden">
            <div className="h-full bg-[color:var(--bl-blue)]" style={{ width: `${(p.revenue / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

export default function AdminStats({ stats, onOpenInventory }) {
  if (!stats) return null;
  return (
    <div className="space-y-8" data-testid="admin-dashboard-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testid="stat-revenue" label="Total Revenue" value={`A$${stats.total_revenue.toFixed(2)}`}
          sub={`${stats.order_count} paid orders`} icon={DollarSign} accent="gold" />
        <StatCard testid="stat-week" label="Last 7 Days" value={`A$${stats.week_revenue.toFixed(2)}`} sub="rolling window"
          icon={TrendingUp} accent="blue" />
        <StatCard testid="stat-aov" label="Avg Order Value" value={`A$${stats.avg_order_value.toFixed(2)}`} icon={Package} accent="blue" />
        <StatCard testid="stat-unfulfilled" label="Unfulfilled" value={stats.unfulfilled_count}
          sub={stats.pending_count ? `${stats.pending_count} pending payment` : "all paid orders shipped"}
          icon={AlertCircle} accent={stats.unfulfilled_count ? "gold" : "green"} />
      </div>

      {stats.low_stock_count > 0 && (
        <button onClick={onOpenInventory} data-testid="dashboard-low-stock-alert"
          className="w-full text-left border border-[color:var(--bl-gold)]/40 bg-[color:var(--bl-gold)]/5 p-4 flex items-center justify-between hover:bg-[color:var(--bl-gold)]/10 transition-colors">
          <div className="flex items-center gap-3">
            <Warehouse size={16} className="text-[color:var(--bl-gold)]" />
            <div>
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-[color:var(--bl-gold)]">Low Stock Alert</div>
              <div className="text-white/70 text-sm mt-0.5">{stats.low_stock_count} variant{stats.low_stock_count === 1 ? "" : "s"} at or below {LOW_STOCK_UI_THRESHOLD} units — tap to manage</div>
            </div>
          </div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">Open Inventory →</div>
        </button>
      )}

      <div className="border border-white/8 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="bl-eyebrow text-[color:var(--bl-gold)]">Revenue · Last 7 Days</div>
            <div className="font-display text-white text-2xl mt-1">A${stats.week_revenue.toFixed(2)}</div>
          </div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">AUD</div>
        </div>
        <Sparkline series={stats.week_series} />
      </div>

      <div className="border border-white/8 p-6">
        <div className="bl-eyebrow text-[color:var(--bl-gold)] mb-5">Top Products</div>
        <TopProducts products={stats.top_products} />
      </div>
    </div>
  );
}
